'use strict';

const _template = require('lodash/template');
const _find = require('lodash/find');
const _findKey = require('lodash/findKey');
const _invert = require('lodash/invert');
const _get = require('lodash/get');
const _has = require('lodash/has');
const _escape = require('lodash/escape');
const _includes = require('lodash/includes');
const _isPlainObject = require('lodash/isPlainObject');
const _last = require('lodash/last');
const _startsWith = require('lodash/startsWith');
const _dropRight = require('lodash/dropRight');
const _drop = require('lodash/drop');
const _join = require('lodash/join');

const externalRegx = /^(http|https):\/\/(.*)/;
const mailtoRegx = /^mailto:([^\?]*)(?:\?subject=(.*)?)?/;
const pageRegx = /^\/(.*)/;
const pageTopRegx = /^(\/?)(.*)#(top|SCROLL_TO_TOP)/;
const pageBottomRegx = /^(\/?)(.*)#(bottom|SCROLL_TO_BOTTOM)/;
const documentRegx = /^document:\/\/(.*)/;
const phoneRegx = /^tel:(.*)/;
const pageWithAnchor = /^\/(.*)#(.*)/;
const samePageWithAnchor = /^#(.*)/;
const ANCHOR_TYPE_TO_NAME = {
  SCROLL_TO_TOP: 'top',
  SCROLL_TO_BOTTOM: 'bottom'
};
const LinkType = {
  PAGE: 'PageLink',
  DYNAMIC_PAGE: 'DynamicPageLink',
  EXTERNAL: 'ExternalLink',
  EMAIL: 'EmailLink',
  DOCUMENT: 'DocumentLink',
  PHONE: 'PhoneLink',
  PAGE_TOP: 'AnchorLink',
  PAGE_BOTTOM: 'AnchorLink',
  ANCHOR: 'AnchorLink'
};

const SUPPORTED_TARGETS = ['_blank', '_self'];

const MASTER_PAGE_ID = 'masterPage';

const anchorLinkBaseTemplate = _template('/<%=linkObject.pageId%>#<%=linkObject.anchorDataId%>', {variable: 'linkObject'});
const pageLinkBaseTemplate = _template('/<%=linkObject.pageId%>', {variable: 'linkObject'});

function getInnerRoute(parts, hasAnchor) {
  const partsWithoutPrefixAndSuffix = _drop(hasAnchor ? _dropRight(parts) : parts);
  return _join(partsWithoutPrefixAndSuffix, '/');
}

const LINK_URL_TEMPLATES = {
  PageLink: {
    url: getPageLinkTemplate, link(type, parts) {
      return {
        type,
        pageId: '#' + parts[1]
      };
    }, regex: [pageRegx]
  },
  DocumentLink: {
    url: _template('document://<%=linkObject.docId%>', {variable: 'linkObject'}), link(type, parts) {
      return {
        type,
        docId: parts[1]
      };
    }, regex: [documentRegx]
  },
  PhoneLink: {
    url: _template('tel:<%=linkObject.phoneNumber%>', {variable: 'linkObject'}), link(type, parts) {
      return {
        type,
        phoneNumber: parts[1]
      };
    }, regex: [phoneRegx]
  },
  EmailLink: {
    url: getEmailLinkTemplate,
    link(type, parts) {
      const data = {
        type,
        recipient: _escape(parts[1])
      };
      if (parts[2]) {
        data.subject = _escape(parts[2]);
      }
      return data;
    },
    regex: [mailtoRegx]
  },
  ExternalLink: {
    url: _template('<%=linkObject.url%>', {variable: 'linkObject'}), link(type, parts, routersMap, target) {
      if (target && !_includes(SUPPORTED_TARGETS, target)) {
        throw new Error('Unsupported target value');
      }
      return {
        type,
        url: _template('<%= partA %>://<%= partB%>')({partA: parts[1], partB: parts[2]}),
        target: target || '_blank'
      };
    }, regex: [externalRegx]
  },
  DynamicPageLink: {
    url: getDynamicPageLinkTemplate, link(type, parts, routersMap) {
      const hasAnchor = _startsWith(_last(parts), '#');
      const data = {
        type,
        innerRoute: getInnerRoute(parts, hasAnchor) || '/',
        routerId: _findKey(routersMap, {prefix: parts[0]})
      };
      if (hasAnchor) {
        const anchorName = _last(parts).replace('#', '');
        data.anchorDataId = _get(_invert(ANCHOR_TYPE_TO_NAME), anchorName, anchorName);
      }
      return data;
    }, regex: [pageTopRegx, pageBottomRegx, pageWithAnchor, pageRegx]
  },
  AnchorLink: {
    url: getAnchorLinkTemplate, link(type, parts) {
      let pageId, anchorDataId;
      const isSamePageLink = parts.length === 2;

      if (isSamePageLink) {
        anchorDataId = parts[1];
        pageId = MASTER_PAGE_ID;
      } else {
        pageId = parts[1];
        anchorDataId = parts[2];
      }

      return {
        type,
        pageId: '#' + pageId,
        anchorDataId: _invert(ANCHOR_TYPE_TO_NAME)[anchorDataId] || anchorDataId
      };
    }, regex: [samePageWithAnchor, pageWithAnchor]
  }
};

function getUrlParts(regExpArr, value, type) {
  if (type === LinkType.DYNAMIC_PAGE) {
    return getDynamicPageParts(value);
  }
  const regexp = _find(regExpArr, function (regExpItem) {
    return regExpItem.test(value);
  });
  let parts = value.match(regexp);
  parts = parts ? parts : [];
  return parts;
}

function getDynamicPageParts(str) {
  return str.match(pageRegx)[1].replace('#', '/#').split(/[\/]+/);
}

function getAnchorName(anchorDataId) {
  return ANCHOR_TYPE_TO_NAME[anchorDataId] || anchorDataId;
}

function getDynamicPagePrefix(routersMap, linkObject) {
  const routerConfig = routersMap[linkObject.routerId];
  return routerConfig && routerConfig.prefix;
}

function getDynamicPageLinkTemplate(linkObject, routersMap) {
  const dynamicPageSuffix = !linkObject.innerRoute || linkObject.innerRoute === '/' ? '' : '/' + linkObject.innerRoute;
  const customLinkObject = {
    pageId: getDynamicPagePrefix(routersMap, linkObject) + dynamicPageSuffix,
    anchorDataId: getAnchorName(linkObject.anchorDataId)
  };
  return customLinkObject.anchorDataId ? anchorLinkBaseTemplate(customLinkObject) : LINK_URL_TEMPLATES.PageLink.url(customLinkObject); //eslint-disable-line new-cap
}

function getPageLinkTemplate(linkObject) {
  return pageLinkBaseTemplate({
    pageId: getPageIdForURL(linkObject.pageId)
  });
}

function getEmailLinkTemplate(linkObject) {
  let templateString = 'mailto:<%=linkObject.recipient%>';
  if (_has(linkObject, 'subject')) {
    templateString += '?subject=<%=linkObject.subject%>';
  }

  return _template(templateString, {variable: 'linkObject'})(linkObject);
}

function getAnchorLinkTemplate(linkObject) {
  const anchorName = getAnchorName(linkObject.anchorDataId);

  if (isMasterPage(extractPageId(linkObject.pageId))) {
    return '#' + anchorName;
  }

  return anchorLinkBaseTemplate({
    pageId: getPageIdForURL(linkObject.pageId),
    anchorDataId: anchorName
  });
}

function getPageIdForURL(pageId) {
  pageId = extractPageId(pageId);
  if (isMasterPage(pageId)) {
    return '';
  }

  return pageId.replace('#', '');
}

function extractPageId(pageId) {
  if (!pageId) {
    return pageId;
  }

  if (_isPlainObject(pageId)) {
    if (pageId.pageUriSEO) {
      return pageId.pageUriSEO;
    } else if (isMasterPage(pageId.id)) {
      return MASTER_PAGE_ID;
    }

    throw new Error('pageId object should contain pageUriSEO');
  }

  return pageId;
}

function isDynamicPage(urlParts, routersMap) {
  return _find(routersMap, {prefix: urlParts[0]});
}

function isMasterPage(pageId) {
  return pageId.replace('#', '') === MASTER_PAGE_ID;
}

function getLinkType(str, routersMap) {
  switch (true) {
  case externalRegx.test(str):
    return LinkType.EXTERNAL;
  case mailtoRegx.test(str):
    return LinkType.EMAIL;
  case samePageWithAnchor.test(str):
    return LinkType.ANCHOR;
  case pageTopRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), routersMap) ? LinkType.DYNAMIC_PAGE : LinkType.PAGE_TOP;
  case pageBottomRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), routersMap) ? LinkType.DYNAMIC_PAGE : LinkType.PAGE_BOTTOM;
  case pageWithAnchor.test(str):
    return isDynamicPage(getDynamicPageParts(str), routersMap) ? LinkType.DYNAMIC_PAGE : LinkType.ANCHOR;
  case pageRegx.test(str):
    return isDynamicPage(getDynamicPageParts(str), routersMap) ? LinkType.DYNAMIC_PAGE : LinkType.PAGE;
  case documentRegx.test(str):
    return LinkType.DOCUMENT;
  case phoneRegx.test(str):
    return LinkType.PHONE;
  default:
    throw new Error('Unsupported link type');
  }
}

function convertLinkObjectToUrl(routersMap, linkObject) {
  if (!linkObject || !_get(LINK_URL_TEMPLATES, [linkObject.type, 'url'])) {
    throw new Error('Provided link type is not supported');
  }
  const urlTemplate = _get(LINK_URL_TEMPLATES, [linkObject.type, 'url']);
  return urlTemplate(linkObject, routersMap);
}

function convertUrlToLinkObject(routersMap, url, target) {
  const type = getLinkType(url, routersMap);
  const urlParts = getUrlParts(LINK_URL_TEMPLATES[type].regex, url, type);
  const linkTemplate = _get(LINK_URL_TEMPLATES, [type, 'link']);
  return linkTemplate(type, urlParts, routersMap, target);
}

function getAnchorDataId(linkItem) {
  return getAnchorName(linkItem.anchorDataId);
}

function getPageId(linkItem) {
  return extractPageId(linkItem.pageId);
}

module.exports = {
  convertLinkObjectToUrl,
  convertUrlToLinkObject,
  getAnchorDataId,
  getPageId
};



// WEBPACK FOOTER //
// ./src/linkUtils.js